import React from "react"
import { Tag, FileText, Folder, FileCode, Hash } from "lucide-react"
import { getDocumentDisplayIcon } from "@/lib/document-icons"
import { compactSearchMatch } from "@/lib/compact-search-match"
import { StorageKeys } from "@/lib/storage"

// Structured query options (like Obsidian)
export const queryOptions = [
  { key: "tag", label: "tag:", description: "search for tags", icon: <Tag className="h-4 w-4" /> },
  { key: "file", label: "file:", description: "match file name", icon: <FileText className="h-4 w-4" /> },
  { key: "path", label: "path:", description: "match path of the file", icon: <Folder className="h-4 w-4" /> },
  { key: "project", label: "project:", description: "search in project", icon: <Folder className="h-4 w-4" /> },
  { key: "type", label: "type:", description: "filter by document type", icon: <FileCode className="h-4 w-4" /> },
  { key: "status", label: "status:", description: "filter by status", icon: <Hash className="h-4 w-4" /> },
  { key: "assignee", label: "assignee:", description: "filter by assignee", icon: <Hash className="h-4 w-4" /> },
]

export const COMMAND_PALETTE_BASECAMP_URL_KEY = 'flownotes:cp-basecamp-url'

export const MAX_HISTORY_SIZE = 50

export function getSearchHistory(): string[] {
  try {
    const stored = localStorage.getItem(StorageKeys.COMMAND_PALETTE_SEARCH_HISTORY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
        return parsed
      }
    }
    return []
  } catch (err) {
    console.warn('Failed to load search history:', err)
    return []
  }
}

export function saveSearchHistory(history: string[]): void {
  try {
    localStorage.setItem(StorageKeys.COMMAND_PALETTE_SEARCH_HISTORY, JSON.stringify(history))
  } catch (err) {
    console.warn('Failed to save search history:', err)
  }
}

export function addToSearchHistory(query: string): void {
  if (!query.trim()) return

  const history = getSearchHistory()

  if (history[0] === query) return

  const newHistory = [query, ...history.filter(h => h !== query)].slice(0, MAX_HISTORY_SIZE)
  saveSearchHistory(newHistory)
}

export function extractFilenameFromPath(input: string): string | null {
  const isContentRelativePath = /^\/(?:personal|clients|projects|hop-studios|ideas)\//.test(input)

  const isAbsoluteSystemPath = (
    input.startsWith('/Users/') ||
    input.startsWith('/home/') ||
    input.startsWith('~') ||
    /^[A-Z]:\\/.test(input)
  )

  const isRelativePath = (
    (input.includes('/') || input.includes('\\')) &&
    /\.(md|txt|tsx?|jsx?|json|ya?ml)$/i.test(input)
  )

  // Bare filename without slashes (e.g. "branding-strategy.md") - common when
  // user remembers a doc name but not its path. Without this, full-text search
  // tokenizes the hyphens and `.md` into bad query terms.
  const isBareFilename = (
    !input.includes('/') &&
    !input.includes('\\') &&
    /\.(md|markdown)$/i.test(input)
  )

  const isFullPath = isContentRelativePath || isAbsoluteSystemPath || isRelativePath || isBareFilename

  if (isFullPath) {
    const segments = input.split(/[/\\]/)
    const filename = segments[segments.length - 1]
    return filename || null
  }

  return null
}

export function extractBasecampIdFromFilename(filename: string): string | null {
  const match = filename.match(/^(\d{10,})/)
  return match ? match[1] : null
}

export function extractBasecampId(input: string): { id: string | null; type: 'todo' | 'message' | 'inbox_forward' | null } {
  if (input.startsWith("http://") || input.startsWith("https://")) {
    const cleanInput = input.split('#')[0].split('?')[0];

    const todoMatch = cleanInput.match(/\/todos\/(\d+)/)
    if (todoMatch) {
      return { id: todoMatch[1], type: 'todo' }
    }

    const messageMatch = cleanInput.match(/\/messages\/(\d+)/)
    if (messageMatch) {
      return { id: messageMatch[1], type: 'message' }
    }

    const forwardMatch = cleanInput.match(/\/inbox_forwards\/(\d+)/)
    if (forwardMatch) {
      return { id: forwardMatch[1], type: 'inbox_forward' }
    }

    return { id: null, type: null }
  }

  if (/^\d{10,}$/.test(input.trim())) {
    return { id: input.trim(), type: 'todo' }
  }

  return { id: null, type: null }
}

export function extractBasecampUrlInfo(url: string): { projectId: string | null; todoId: string | null } {
  if (!url?.trim()) {
    return { projectId: null, todoId: null }
  }

  const clean = url.split('#')[0].split('?')[0]

  const bcMatch = clean.match(/\/(?:buckets|projects)\/(\d+)\/todos\/(\d+)/i)
  if (bcMatch) {
    return { projectId: bcMatch[1], todoId: bcMatch[2] }
  }

  const fnMatch = clean.match(/\/basecamp(?:\/[^/]+)?\/project\/(\d+)\/todo\/(\d+)/i)
  if (fnMatch) {
    return { projectId: fnMatch[1], todoId: fnMatch[2] }
  }

  return { projectId: null, todoId: null }
}

export function getDocumentIcon(type: string): React.ReactNode {
  return getDocumentDisplayIcon({ type, kind: type }, { className: 'h-4 w-4' })
}

/**
 * Highlight matching parts of a file path
 * Returns JSX with highlighted matching segments
 */
export function highlightPathMatch(filePath: string, searchQuery: string): React.ReactNode {
  if (!filePath || !searchQuery) {
    return <span className="font-mono text-xs text-muted-foreground">{filePath || ''}</span>
  }

  const searchFilename = extractFilenameFromPath(searchQuery) || searchQuery.trim()
  const normalizedSearch = searchFilename.toLowerCase().replace(/\.(md|markdown)$/i, '')

  const pathSegments = filePath.split('/').filter(s => s)
  const filename = pathSegments[pathSegments.length - 1] || ''
  const filenameWithoutExt = filename.replace(/\.(md|markdown)$/i, '').toLowerCase()

  const filenameMatches =
    compactSearchMatch(filenameWithoutExt, normalizedSearch) ||
    compactSearchMatch(filename, searchFilename)

  if (!filenameMatches) {
    return <span className="font-mono text-xs text-muted-foreground">{filePath}</span>
  }

  const parts: React.ReactNode[] = []

  pathSegments.slice(0, -1).forEach((segment, index) => {
    parts.push(<span key={`path-${index}`} className="text-muted-foreground">{segment}</span>)
    if (index < pathSegments.length - 2) {
      parts.push(<span key={`sep-${index}`} className="text-muted-foreground">/</span>)
    }
  })

  if (pathSegments.length > 1) {
    parts.push(<span key="sep-before-filename" className="text-muted-foreground">/</span>)
  }

  const filenameLower = filename.toLowerCase()
  const searchLower = searchFilename.toLowerCase()
  const matchIndex = filenameLower.indexOf(searchLower)

  if (matchIndex >= 0) {
    const beforeMatch = filename.substring(0, matchIndex)
    const match = filename.substring(matchIndex, matchIndex + searchFilename.length)
    const afterMatch = filename.substring(matchIndex + searchFilename.length)

    parts.push(
      <span key="filename">
        <span className="text-muted-foreground">{beforeMatch}</span>
        <span className="font-bold text-foreground">{match}</span>
        <span className="text-muted-foreground">{afterMatch}</span>
      </span>
    )
  } else {
    parts.push(<span key="filename" className="font-bold text-foreground">{filename}</span>)
  }

  return <span className="font-mono text-xs">{parts}</span>
}
